import { Button, Spinner } from '@consigli/facade';
import { useExportToExcelMutation, usePackageId, useProject, useProjectId } from '@consigli/hooks';
import { Finding } from '@consigli/types';
import { MIME_TYPES, saveBlobBase64 } from '@consigli/utils';
import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { createRfpDeliverablesExport, DeliverableExportItem } from '@/util/excel-export';

import { CategoryToggleButton } from './category-toggle-button';
import { DeliverableCard } from './deliverable-card';
import { RfpDeliverablePdfPanel } from './rfp-deliverable-pdf-panel';

interface DeliverableItem {
  name: string;
  description: string;
  source: string | null;
  pageNumber: number | null;
  blobId: string | null;
  x0: number | null;
  y0: number | null;
  x1: number | null;
  y1: number | null;
}

export interface DeliverablePayload {
  category: string;
  categoryDescription: string;
  deliverables: DeliverableItem[];
}

const isStringOrNull = (value: unknown): value is string | null =>
  value === null || typeof value === 'string';

const isOptionalNumber = (value: unknown): value is number | null =>
  value === null || typeof value === 'number';

const toDeliverableItem = (raw: unknown): DeliverableItem | null => {
  if (typeof raw !== 'object' || raw === null) return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.name !== 'string' || typeof r.description !== 'string') return null;
  return {
    name: r.name,
    description: r.description,
    source: isStringOrNull(r.source) ? r.source : null,
    pageNumber: isOptionalNumber(r.pageNumber) ? r.pageNumber : null,
    blobId: isStringOrNull(r.blobId) ? r.blobId : null,
    x0: isOptionalNumber(r.x0) ? r.x0 : null,
    y0: isOptionalNumber(r.y0) ? r.y0 : null,
    x1: isOptionalNumber(r.x1) ? r.x1 : null,
    y1: isOptionalNumber(r.y1) ? r.y1 : null,
  };
};

const toDeliverablePayload = (value: unknown): DeliverablePayload | null => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const v = value as Record<string, unknown>;
  if (typeof v.category !== 'string' || typeof v.categoryDescription !== 'string') return null;
  if (!Array.isArray(v.deliverables)) return null;
  const deliverables = v.deliverables
    .map(toDeliverableItem)
    .filter((d): d is DeliverableItem => d !== null);
  return {
    category: v.category,
    categoryDescription: v.categoryDescription,
    deliverables,
  };
};

export const parseDeliverablePayload = (finding: Finding): DeliverablePayload | null => {
  const raw = finding.messageContext?.CUSTOM_MESSAGE;
  if (!raw || typeof raw === 'string') return null;
  return toDeliverablePayload(raw);
};

interface SelectedDeliverable {
  blobId: string;
  pageNumber: number | null;
  name: string;
  x0: number | null;
  y0: number | null;
  x1: number | null;
  y1: number | null;
}

interface RfpRequirementsAccordionProps {
  findings: Finding[];
}

export const RfpRequirementsAccordion: FC<RfpRequirementsAccordionProps> = ({ findings }) => {
  const { t } = useTranslation();
  const projectId = useProjectId();
  const packageId = usePackageId();
  const { project } = useProject(projectId);
  const [openCategory, setOpenCategory] = useState<string | null>(null);
  const [selectedDeliverable, setSelectedDeliverable] = useState<SelectedDeliverable | null>(null);
  const [exportToExcel, { isLoading: isExporting }] = useExportToExcelMutation();

  const categories = useMemo(
    () =>
      findings
        .map(parseDeliverablePayload)
        .filter((p): p is DeliverablePayload => p !== null && p.deliverables.length > 0),
    [findings],
  );

  const handleExport = useCallback(async () => {
    if (!project) return;
    try {
      const deliverables: DeliverableExportItem[] = categories.flatMap((payload) =>
        payload.deliverables.map((d) => ({
          category: payload.category,
          name: d.name,
          description: d.description,
          source: d.source,
        })),
      );
      const excelExport = createRfpDeliverablesExport(deliverables, t, project.name);
      const { base64Data } = await exportToExcel({
        projectId,
        packageId,
        excelExport,
      }).unwrap();
      saveBlobBase64(`${project.name}_deliverables.xlsx`, base64Data, MIME_TYPES.xlsx);
      toast.success(t('dashboard.rfp-export.success'));
    } catch {
      toast.error(t('dashboard.rfp-export.error'));
    }
  }, [project, categories, t, exportToExcel, projectId, packageId]);

  if (categories.length === 0) return null;

  return (
    <section
      className="bg-day-light-3 rounded-md p-6 my-6"
      aria-labelledby="rfp-requirements-heading"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 id="rfp-requirements-heading" className="text-xl font-semibold text-day-neutral-subtle">
          {t('dashboard.rfp-requirements.heading')}
        </h3>
        <Button
          secondary
          rounded
          onClick={handleExport}
          disabled={isExporting}
          icon={isExporting ? () => <Spinner size="xsmall" /> : undefined}
          type="button"
        >
          {t('dashboard.rfp-export.export-deliverables')}
        </Button>
      </div>
      <ul className="border border-day-light-2 rounded-md divide-y divide-day-light-2 bg-white overflow-hidden">
        {categories.map((payload) => {
          const isOpen = openCategory === payload.category;
          return (
            <li key={payload.category}>
              <CategoryToggleButton
                category={payload.category}
                categoryDescription={payload.categoryDescription}
                count={payload.deliverables.length}
                isOpen={isOpen}
                onToggle={() => setOpenCategory(isOpen ? null : payload.category)}
              />
              {isOpen && (
                <div className="bg-day-light-5 px-4 py-3 space-y-3">
                  {payload.deliverables.map((deliverable, idx) => {
                    const key = `${payload.category}-${deliverable.blobId ?? 'no-blob'}-${idx}`;
                    const isClickable = deliverable.blobId != null;
                    const cardContent = (
                      <>
                        <div className="font-semibold text-day-neutral-dark">
                          {deliverable.name}
                        </div>
                        <div className="text-sm text-day-neutral-subtle mt-1">
                          {deliverable.description}
                        </div>
                        {deliverable.source && (
                          <blockquote className="mt-2 border-l-2 border-day-light-2 pl-3 text-xs italic text-day-neutral-subtle">
                            &ldquo;{deliverable.source}&rdquo;
                          </blockquote>
                        )}
                      </>
                    );

                    return (
                      <DeliverableCard
                        key={key}
                        onClick={
                          isClickable
                            ? () =>
                                setSelectedDeliverable({
                                  blobId: deliverable.blobId!,
                                  pageNumber: deliverable.pageNumber,
                                  name: deliverable.name,
                                  x0: deliverable.x0,
                                  y0: deliverable.y0,
                                  x1: deliverable.x1,
                                  y1: deliverable.y1,
                                })
                            : undefined
                        }
                      >
                        {cardContent}
                      </DeliverableCard>
                    );
                  })}
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {selectedDeliverable && (
        <RfpDeliverablePdfPanel
          blobId={selectedDeliverable.blobId}
          pageNumber={selectedDeliverable.pageNumber}
          name={selectedDeliverable.name}
          x0={selectedDeliverable.x0}
          y0={selectedDeliverable.y0}
          x1={selectedDeliverable.x1}
          y1={selectedDeliverable.y1}
          onClose={() => setSelectedDeliverable(null)}
        />
      )}
    </section>
  );
};
